// Environment Data Models matching C# SDK models

import { ReleasePipelineSource } from '../../AGSS/models/ReleasePipelineSource';

export enum Pipeline {
    Blob = 0,
    Core = 1,
    Message = 2,
}

export enum Region {
    Primary = 0,
    Secondary = 1,
}

export enum WorkLane {
    Warm = 0,
    Dcmt = 1,
}

export interface EnvironmentDataChannel {
    name: string;
    pipeline: Pipeline;
    region: Region;
    workLane: WorkLane;
    isActive: boolean;
}

export interface EnvironmentFlags {
    useDataChannelOverrides?: boolean | null;
    cachingEnabled?: boolean | null;
    messagingEnabled?: boolean | null;
    storageUploadEnabled?: boolean | null;
    octopusEnabled?: boolean | null;
    lockBoxEnabled?: boolean | null;
    egressEnabled?: boolean | null;
    icmEnabled?: boolean | null;
    requiredOnNetworkShare?: boolean | null;
    useDynamicHealthChecks?: boolean | null;
    useSrxV2?: boolean | null;
    blockSrxExecution?: boolean | null;
    messagingHeartbeatEnabled?: boolean | null;
}

export interface DataChannelOverride {
    workLane?: WorkLane | null;
    pipeline?: Pipeline | null;
    region?: Region | null;
    name: string;
    serviceTreeId?: string | null;
    accountName?: string | null;
    projectName?: string | null;
    branchName?: string | null;
    releaseDefinitionId?: string | null;
    pipelineSourceType?: ReleasePipelineSource | null;
    tags?: string[];
    enabled: boolean;
    active: boolean;
    startTime?: string | null;
    endTime?: string | null;
}

export interface FlagOverride {
    name: string;
    serviceTreeId?: string | null;
    accountName?: string | null;
    projectName?: string | null;
    branchName?: string | null;
    releaseDefinitionId?: string | null;
    pipelineSourceType?: ReleasePipelineSource | null;
    tags?: string[];
    enabled: boolean;
    active: boolean;
    startTime?: string | null;
    endTime?: string | null;
    featureFlags: EnvironmentFlags;
}

export interface EnvironmentOperationMode {
    name: string;
    isEnabled: boolean;
    region?: Region | null;
    pipeline?: Pipeline | null;
    worklane?: WorkLane | null;
    channels: EnvironmentDataChannel[];
    dataChannelOverrides: DataChannelOverride[];
    featureFlagsOverrides?: FlagOverride[];
    featureFlags: EnvironmentFlags;
}

export interface EnvironmentData {
    id?: string;
    partitionKey?: string;
    entityType?: string;
    name: string;
    activeOperationalModeName?: string | null;
    normalOperationMode: EnvironmentOperationMode;
    operationModes: EnvironmentOperationMode[];
    innerId?: string;
    createdOn?: string;
}

export interface EnvironmentDataResponse {
    data: EnvironmentData;
    succeeded: boolean;
    message?: string | null;
    statusCode?: number | null;
}

export interface EnvironmentListResponse {
    data: EnvironmentData[];
    succeeded: boolean;
    message?: string | null;
    statusCode?: number | null;
}

/**
 * Health status for a heartbeat uptime bucket / current-status badge, matching C# HeartbeatUptimeStatus.
 * Unknown means "no data" (e.g. MessagingHeartbeatEnabled isn't on for this cloud/region yet) rather than
 * a failure.
 */
export type HeartbeatUptimeStatus =
    | 'Unknown'
    | 'Healthy'
    | 'Degraded'
    | 'Unhealthy';

/**
 * One time-bucketed segment of on-time heartbeat delivery (e.g. 30 minutes of the 24h view, or
 * one 4-hour span of the 7d view).
 */
export interface UptimeBucket {
    bucketStartUtc: string;
    bucketEndUtc: string;
    status: HeartbeatUptimeStatus;
    sentCount: number;
    receivedCount: number;
    // Optional for mixed-version backends: absent means unavailable, not zero.
    matureCount?: number;
    onTimeCount?: number;
    lateCount?: number;
    deadlineMissedCount?: number;
    averageDurationMs?: number | null;
}

export interface HeartbeatWindowSummary {
    sentCount: number;
    completedCount: number;
    pendingCount: number;
    matureCount?: number;
    onTimeCount?: number;
    lateCount?: number;
    deadlineMissedCount?: number;
    averageDurationMs?: number | null;
    medianDurationMs?: number | null;
    p95DurationMs?: number | null;
    averageIngressDurationMs?: number | null;
    medianIngressDurationMs?: number | null;
    p95IngressDurationMs?: number | null;
    averageEgressDurationMs?: number | null;
    medianEgressDurationMs?: number | null;
    p95EgressDurationMs?: number | null;
}

/**
 * Heartbeat round-trip uptime for a single cloud+region pair, over the 24h and 7d windows.
 */
export interface CloudRegionUptime {
    cloudName: string;
    regionMoniker: string;
    isEnabled: boolean;
    currentStatus: HeartbeatUptimeStatus;
    currentStatusLabel: string;
    uptimePercentage24h: number;
    uptimePercentage7d: number;
    lastAckUtc?: string | null;
    lastOnTimeAckUtc?: string | null;
    deliveryDeadlineMinutes?: number;
    summary24h: HeartbeatWindowSummary;
    summary7d: HeartbeatWindowSummary;
    buckets24h: UptimeBucket[];
    buckets7d: UptimeBucket[];
}

/**
 * Payload for the SRMP messaging heartbeat uptime response — all cloud+region pairs at once.
 */
export interface HeartbeatUptimeResponse {
    clouds: CloudRegionUptime[];
    timestamp: string;
}

/**
 * Request Result wrapper for the heartbeat uptime API response, matching the RequestResult<T> shape
 * every other AGCP endpoint returns.
 */
export interface HeartbeatUptimeApiResponse {
    data: HeartbeatUptimeResponse;
    succeeded: boolean;
    message?: string | null;
    statusCode?: number | null;
}
